import { AxiosError } from "axios";

import { publicAxios } from "@/lib/axios";

/**
 * Public runtime client for config-driven lead forms.
 *
 * Separate from `src/api/admin/leadFormConfig.ts` on purpose: that one manages the form
 * DEFINITION behind an admin token, this one is the anonymous visitor filling it in. They
 * share nothing but the word "lead form".
 *
 * Every function returns a discriminated result rather than throwing. A lead form is the
 * revenue path — a rejected step must surface the server's per-field errors in the UI, and
 * an unexpected failure must leave the visitor with something to do next, never a blank
 * screen from an uncaught rejection.
 */

/** Which gate blocked the request, so the UI can jump to it instead of dead-ending. */
export interface OtpRequired {
  step_id: string;
  verifies: string | null;
}

export type StepResult =
  | { ok: true; lead_id: string; answers: Record<string, unknown> }
  | { ok: false; message: string; errors?: Record<string, string>; otpRequired?: OtpRequired };

/**
 * Unwrap the server's error envelope.
 *
 * `ServiceError` does `super(JSON.stringify(message))`, and `APIResponse` then puts that
 * raw `error.message` on the wire — so what arrives is a JSON *string*, even when the
 * controller passed an object. A plain string message arrives double-encoded as `"\"…\""`.
 * Parsing is therefore the normal path, not the exceptional one; reading `message`
 * directly would show visitors a blob of JSON.
 */
const errorPayload = (error: unknown): Record<string, unknown> => {
  const err = error as AxiosError<{ error?: { message?: unknown } }>;
  const raw = err?.response?.data?.error?.message;

  if (raw && typeof raw === "object") return raw as Record<string, unknown>;

  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed as Record<string, unknown>;
      if (typeof parsed === "string") return { message: parsed };
    } catch {
      // Not JSON — an older path or a proxy's own error body. Use it as-is.
    }
    return { message: raw };
  }

  return {
    message: err?.response
      ? "Something went wrong. Please try again."
      : "We could not reach the server. Check your connection and try again.",
  };
};

/**
 * Submit one step.
 *
 * `leadId` is OPTIONAL on the step that collects the mobile — that step mints the lead row
 * and returns its id, which the caller must carry for every later step.
 */
export async function submitLeadFormStep(params: {
  stepId: string;
  configId: number;
  values: Record<string, unknown>;
  leadId?: string | null;
  captureSessionId?: string;
  context?: Record<string, unknown>;
}): Promise<StepResult> {
  try {
    const res = await publicAxios.put(`/lead-form/step/${encodeURIComponent(params.stepId)}`, {
      lead_id: params.leadId ?? undefined,
      config_id: params.configId,
      values: params.values,
      capture_session_id: params.captureSessionId,
      context: params.context,
    });
    const data = res?.data?.data ?? {};
    return { ok: true, lead_id: String(data.lead_id ?? ""), answers: data.answers ?? {} };
  } catch (error) {
    const payload = errorPayload(error);
    return {
      ok: false,
      message: String(payload.message ?? "Could not save this step"),
      errors: (payload.errors as Record<string, string>) ?? undefined,
      otpRequired: (payload.otpRequired as OtpRequired) ?? undefined,
    };
  }
}

/** Issue the OTP for a `kind: "otp"` step. The code is never returned to the client. */
export async function sendLeadFormOtp(params: {
  leadId: string;
  configId: number;
  stepId: string;
}): Promise<{ ok: boolean; message?: string }> {
  try {
    await publicAxios.post("/lead-form/otp/send", {
      lead_id: params.leadId,
      config_id: params.configId,
      step_id: params.stepId,
    });
    return { ok: true };
  } catch (error) {
    return { ok: false, message: String(errorPayload(error).message) };
  }
}

export async function verifyLeadFormOtp(params: {
  leadId: string;
  configId: number;
  stepId: string;
  otp: string;
}): Promise<{ ok: boolean; firstVerification?: boolean; message?: string }> {
  try {
    const res = await publicAxios.post("/lead-form/otp/verify", {
      lead_id: params.leadId,
      config_id: params.configId,
      step_id: params.stepId,
      otp: params.otp,
    });
    return { ok: true, firstVerification: res?.data?.data?.firstVerification === true };
  } catch (error) {
    return { ok: false, message: String(errorPayload(error).message) };
  }
}

/**
 * Finish the journey. This is the ONLY call that reaches the CRM, and it is idempotent —
 * a double-click or a retried request cannot produce a second drop.
 */
export async function completeLeadForm(params: {
  leadId: string;
  configId: number;
  isPreview?: boolean;
  /**
   * `submit` is the ONE drop that creates the CRM record; `sync` enriches it from a step
   * that comes after. The server refuses to create a record in `sync` mode, so a mislabelled
   * call can never move where submission happens.
   */
  mode?: "submit" | "sync";
}): Promise<{
  ok: boolean;
  leadsId?: string | null;
  alreadyDelivered?: boolean;
  synced?: boolean;
  message?: string;
  /** OMS one-time token and the cookie name to store it under. Null when unavailable. */
  token?: string | null;
  cookieKey?: string | null;
  /** `/application/{pok}?locale=in` for this lead's course, or null. */
  applicationRedirectUrl?: string | null;
}> {
  try {
    const res = await publicAxios.post("/lead-form/complete", {
      lead_id: params.leadId,
      config_id: params.configId,
      isPreview: params.isPreview,
      mode: params.mode,
    });
    const data = res?.data?.data ?? {};
    return {
      ok: true,
      leadsId: data.leads_id ?? null,
      alreadyDelivered: data.alreadyDelivered === true,
      synced: data.synced === true,
      token: data.token ?? null,
      cookieKey: data.cookieKey ?? null,
      applicationRedirectUrl: data.applicationRedirectUrl ?? null,
    };
  } catch (error) {
    return { ok: false, message: String(errorPayload(error).message) };
  }
}

/**
 * Resolve a dropdown's option values.
 *
 * Unauthenticated by design: these are the very lists the visitor is about to be shown, so
 * there is nothing to protect, and gating them would stop the form rendering its own
 * selects. Returns [] on failure — a dropdown that cannot load must not take the form down
 * with it.
 */
/**
 * One entry in a source-backed dropdown.
 *
 * `brochure` is present on course options that have one. It travels with the option
 * rather than being fetched later because the download flow has to know, at the moment a
 * visitor picks a course, which file to hand them.
 */
export interface LeadFormOption {
  value: string;
  label: string;
  brochure?: string;
  /** A course's programme key. Present only when the course has one. */
  pok?: string;
}

export async function resolveLeadFormOptions(params: {
  source: string;
  universityId: number;
  group?: string;
  dependsOnValue?: string;
}): Promise<LeadFormOption[]> {
  try {
    const res = await publicAxios.get("/lead-form-options/resolve", {
      params: {
        source: params.source,
        university_id: params.universityId,
        group: params.group || undefined,
        depends_on_value: params.dependsOnValue || undefined,
      },
    });
    const rows = res?.data?.data;
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}
